import { formatEnumForDisplay } from '@/utils';

// FleetHealthFilters represents the nested structure of deployment types and service groups
// Deployment types (e.g., "coatings_manager", "structure") always contain service groups
// Service groups (e.g., "other_services", "unknown_services") contain RI levels
// Example: { coatings_manager: { other_services: { ri0: 973.31, ri1: 480.94 }, unknown_services: { ri0: 1636.67 } } }
export type FleetHealthFilters = Record<string, Record<string, Record<string, number>>>;

interface FleetHealthCategory {
  id: string; // e.g., "pressure", "other_services", "structure"
  name: string; // e.g., "Pressure", "Other Services", "Structure"
  type: 'deployment' | 'service_group'; // Type of category
  parentDeployment?: string; // For service groups, which deployment they belong to
  availableRiLevels: Set<string>; // Which RI levels are available (ri0-ri5)
}

interface ExtractedFleetHealthData {
  categories: FleetHealthCategory[];
  categoryMap: Map<string, FleetHealthCategory>; // Quick lookup by id
}

/**
 * Extracts all unique deployment types and service groups from all platforms
 * Flattens the structure so service groups are top-level categories
 */
export function extractFleetHealthCategories(
  platforms: Array<{ fleetHealthFilters?: FleetHealthFilters }>
): ExtractedFleetHealthData {
  const categoryMap = new Map<string, FleetHealthCategory>();

  platforms.forEach((platform) => {
    const filters = platform.fleetHealthFilters;
    if (!filters || typeof filters !== 'object') return;

    Object.entries(filters as FleetHealthFilters).forEach(([deploymentType, serviceGroups]) => {
      // Add deployment type as a category
      if (!categoryMap.has(deploymentType)) {
        categoryMap.set(deploymentType, {
          id: deploymentType,
          name: formatEnumForDisplay(deploymentType),
          type: 'deployment',
          availableRiLevels: new Set(),
        });
      }

      const deploymentCategory = categoryMap.get(deploymentType)!;

      // Deployment types don't have direct RI levels, only service groups do
      // But we collect all RI levels from service groups for the deployment category
      if (typeof serviceGroups !== 'object' || serviceGroups === null) return;

      // Extract service groups and add them as separate categories
      Object.entries(serviceGroups).forEach(([serviceGroupId, riLevels]) => {
        if (typeof riLevels === 'object' && riLevels !== null) {
          // This is a service group with RI levels

          if (!categoryMap.has(serviceGroupId)) {
            categoryMap.set(serviceGroupId, {
              id: serviceGroupId,
              name: formatEnumForDisplay(serviceGroupId),
              type: 'service_group',
              parentDeployment: deploymentType,
              availableRiLevels: new Set(),
            });
          }

          const serviceGroupCategory = categoryMap.get(serviceGroupId)!;

          // Collect RI levels for this service group
          Object.keys(riLevels).forEach((riKey) => {
            if (riKey.startsWith('ri')) {
              serviceGroupCategory.availableRiLevels.add(riKey);
              // Also add to parent deployment category
              deploymentCategory.availableRiLevels.add(riKey);
            }
          });
        }
      });
    });
  });

  return {
    categories: Array.from(categoryMap.values()),
    categoryMap,
  };
}

/**
 * Extracts the first fieldName found from any deployment type in fleetHealthFilters
 * Returns formatted name (e.g., "eq_card_corrosion" -> "Eq Card Corrosion")
 */
export function extractServiceGroupFieldName(
  platforms: Array<{ fleetHealthFilters?: FleetHealthFilters }>
): string | undefined {
  for (const platform of platforms) {
    const filters = platform.fleetHealthFilters;
    if (!filters || typeof filters !== 'object') continue;

    for (const deploymentData of Object.values(filters)) {
      if (
        deploymentData &&
        typeof deploymentData === 'object' &&
        'fieldName' in deploymentData &&
        typeof deploymentData.fieldName === 'string'
      ) {
        return formatEnumForDisplay(deploymentData.fieldName);
      }
    }
  }
  return undefined;
}
